import React, { useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Typography from '@tiptap/extension-typography'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'

const { 
  FiBold, FiItalic, FiUnderline, FiLink, FiList, FiCode,
  FiType, FiAlignLeft, FiMinus, FiQuote
} = FiIcons

// Utility function to normalize content to a valid TipTap JSON document
const normalizeContent = (content) => {
  // Default empty TipTap document
  const emptyDoc = { type: 'doc', content: [{ type: 'paragraph' }] }
  
  // If content is falsy, return empty doc
  if (!content) {
    return emptyDoc
  }
  
  // If content is already an object
  if (typeof content === 'object') {
    // Check if it's a valid TipTap document
    if (content.type === 'doc' && Array.isArray(content.content)) {
      return content
    }
    // Check if it has a doc field that looks like TipTap
    if (content.doc && content.doc.type === 'doc' && Array.isArray(content.doc.content)) {
      return content.doc
    }
    // If it's an object but not TipTap format, return empty doc
    return emptyDoc
  }
  
  // If content is a string, try to parse it
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content)
      // Recursively normalize the parsed content
      return normalizeContent(parsed)
    } catch (error) {
      console.warn('Failed to parse content as JSON, using empty document:', error)
      return emptyDoc
    }
  }
  
  // For any other type, return empty doc
  return emptyDoc
}

const RichContentEditor = ({ content, onChange, placeholder = "Start writing..." }) => {
  const normalizedContent = normalizeContent(content)
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline'
        }
      }),
      Placeholder.configure({
        placeholder
      }),
      Typography
    ],
    content: normalizedContent,
    onUpdate: ({ editor }) => {
      const json = editor.getJSON()
      onChange(JSON.stringify(json))
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[400px] p-4'
      }
    }
  })

  useEffect(() => {
    if (editor && content) {
      const currentEditorContent = editor.getJSON()
      const newNormalizedContent = normalizeContent(content)
      
      // Only update if the content is actually different to prevent cursor jumps
      if (JSON.stringify(currentEditorContent) !== JSON.stringify(newNormalizedContent)) {
        try {
          editor.commands.setContent(newNormalizedContent)
        } catch (error) {
          console.warn('Error setting editor content, using empty document:', error)
          editor.commands.setContent({ type: 'doc', content: [{ type: 'paragraph' }] })
        }
      }
    }
  }, [content, editor])

  if (!editor) {
    return <div className="h-64 bg-gray-50 rounded-lg animate-pulse" />
  }

  const addLink = () => {
    const url = window.prompt('Enter URL:')
    if (url) {
      editor.chain().focus().setLink({ href: url }).run()
    }
  }

  const ToolbarButton = ({ onClick, isActive, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-2 rounded-md text-sm font-medium transition-colors ${
        isActive 
          ? 'bg-blue-100 text-blue-700' 
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  )

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-3 border-b border-gray-200 bg-gray-50 flex-wrap">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          H1
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          H2
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          H3
        </ToolbarButton>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold"
        >
          <SafeIcon icon={FiBold} className="w-4 h-4" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic"
        >
          <SafeIcon icon={FiItalic} className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <SafeIcon icon={FiList} className="w-4 h-4" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <span className="text-xs font-bold">1.</span>
        </ToolbarButton>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        <ToolbarButton
          onClick={addLink}
          isActive={editor.isActive('link')}
          title="Add Link"
        >
          <SafeIcon icon={FiLink} className="w-4 h-4" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Quote"
        >
          <SafeIcon icon={FiQuote} className="w-4 h-4" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          isActive={editor.isActive('codeBlock')}
          title="Code Block"
        >
          <SafeIcon icon={FiCode} className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal Rule"
        >
          <SafeIcon icon={FiMinus} className="w-4 h-4" />
        </ToolbarButton>
      </div>

      {/* Editor */}
      <EditorContent editor={editor} />
    </div>
  )
}

export default RichContentEditor